import { createTheme } from '@mui/material/styles';
import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { useDemoRouter } from '@toolpad/core/internal';
import PropTypes from 'prop-types';
import * as React from 'react';
import { triggerGTMEvent } from './GTMEvent';

// Import custom page components
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GavelIcon from '@mui/icons-material/Gavel';
import HomeIcon from '@mui/icons-material/Home';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import PolicyIcon from '@mui/icons-material/Policy';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import Credits from '../pages/Credits';
import HomePage from '../pages/Home';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import TermsAndConditions from '../pages/Terms';
import EMICalculator from '../pages/emi-calculator/emi-calculator-component';
import LumpsumInvestmentCalculator from '../pages/lumpsuminvestment-calculator/lumpsuminvestment-calculator-component';
import PrepaymentCalculator from '../pages/prepayment-calculator/prepayment-calculator-component';
import SipInvestmentCalculator from '../pages/sipinvestment-calculator/sipinvestment-calculator-component';

const NAVIGATION = [
  { segment: 'home', title: 'Home', icon: <HomeIcon /> },
  { kind: 'divider' },
  { kind: 'header', title: 'Loan Calculators', },
  { segment: 'emi-calculator', title: 'Loan EMI', icon: <AccountBalanceIcon /> },
  { segment: 'prepayment-calculator', title: 'Loan Prepayment', icon: <AccountBalanceIcon /> },
  { kind: 'divider' },
  { kind: 'header', title: 'Investment Calculators', },
  { segment: 'lumpsuminvestment-calculator', title: 'Lumpsum Investment', icon: <ShowChartIcon /> },
  { segment: 'sipinvestment-calculator', title: 'SIP Investment', icon: <ShowChartIcon /> },
  { kind: 'divider' },
  { segment: 'terms', title: 'Terms & Conditions', icon: <GavelIcon /> },
  { segment: 'privacy', title: 'Privacy Policy', icon: <PolicyIcon /> },
  { segment: 'credits', title: 'Credits', icon: <InsertLinkIcon /> },
];

const demoTheme = createTheme({
  colorSchemes: {
    light: {
      palette: {
        background: {
          default: '#F0F2F5',  // Page background
          paper: '#F0F2F5',    // Background for panels like the left sidebar
        },
        appBar: {
          main: '#F0F2F5',     // Top header background color
        },
      },
    },
    dark: false,  // Keeps the default dark theme
  },
  breakpoints: {
    values: { xs: 0, sm: 600, md: 600, lg: 1200, xl: 1536 },
  },
});


function DashboardLayoutBasic(props) {
  const { window } = props;
  const router = useDemoRouter(); // Default to dashboard

  const gaEventOfDashboard = (path) => {
    switch (path) {
      case '/home':
        return 'ADMINPANEL > Home';
      case '/terms':
        return 'ADMINPANEL > Terms & Conditions';
      case '/privacy':
        return 'ADMINPANEL > Privacy Policy';
      case '/credits':
        return 'ADMINPANEL > Credits';
      case '/prepayment-calculator':
        return 'ADMINPANEL > Loan Prepayment';
      case '/emi-calculator':
        return 'ADMINPANEL > Loan EMI';
      case '/lumpsuminvestment-calculator':
        return 'ADMINPANEL > Lumpsum Investment';
      case '/sipinvestment-calculator':
        return 'ADMINPANEL > SIP Investment';
      default:
        return 'ADMINPANEL > Default';
    }
  }


  // Function to return the correct component based on pathname
  const renderContent = () => {
    const dashboardEvent = gaEventOfDashboard(router.pathname);
    triggerGTMEvent("Dashboard", dashboardEvent);

    switch (router.pathname) {
      case '/home':
        return <HomePage router={router} />;
      case '/terms':
        return <TermsAndConditions router={router} />;
      case '/privacy':
        return <PrivacyPolicy router={router} />;
      case '/credits':
        return <Credits router={router} />;
      case '/prepayment-calculator':
        return <PrepaymentCalculator router={router} />;
      case '/emi-calculator':
        return <EMICalculator router={router} />;
      case '/lumpsuminvestment-calculator':
        return <LumpsumInvestmentCalculator router={router} />;
      case '/sipinvestment-calculator':
        return <SipInvestmentCalculator router={router} />;
      default:
        return <HomePage router={router} />;
    }
  };
  const branding = {
    logo: <img src="/budget.png" alt="App Logo" style={{ width: 32, height: 32 }} />,
    title: 'FinCals',
    homeUrl: '/home',
  };

  return (
    <AppProvider navigation={NAVIGATION} branding={branding} router={router} theme={demoTheme} window={window ? window() : undefined}>
      <DashboardLayout
        defaultSidebarCollapsed={true}
      >
        {renderContent()}
      </DashboardLayout>
    </AppProvider>
  );
}

DashboardLayoutBasic.propTypes = {
  window: PropTypes.func,
};

export default DashboardLayoutBasic;
